/**
 * Page Annuaire Clients - vue light de recherche rapide
 * - Recherche unifiee (nom, email, telephone, adresse, ville, code postal, notes)
 * - Affichage par carte : contact + projets actifs + statut
 * - Liens directs vers la fiche qualif / projet / mailto / tel
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { BranchBadge, BranchPageHeader, BranchFilter, useBranchFilter } from './components/BranchUI';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  a_qualifier: { label: 'A qualifier', color: 'bg-gray-100 text-gray-700' },
  en_qualification: { label: 'En qualification', color: 'bg-blue-100 text-blue-700' },
  qualifie: { label: 'Qualifie', color: 'bg-emerald-100 text-emerald-700' },
  accepte: { label: 'Accepte', color: 'bg-emerald-100 text-emerald-700' },
  rejete: { label: 'Refuse', color: 'bg-red-100 text-red-700' },
};

const ClientsDirectoryPage = () => {
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterArchived, setFilterArchived] = useState(false);
  const [user, setUser] = useState(null);
  const [branchFilter, setBranchFilter] = useBranchFilter('all');
  const debounceRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/auth/me`).then(r => setUser(r.data)).catch(() => {});
  }, []);

  const fetchData = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('search', q);
      params.set('limit', '500');
      const r = await axios.get(`${API}/crm/directory?${params}`);
      setItems(r.data?.items || []);
      setCount(r.data?.count || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchData(search), 250);
    return () => clearTimeout(debounceRef.current);
  }, [search, fetchData]);

  const visibleByArchive = items.filter(i => filterArchived ? true : !i.archived);
  const visible = (user?.cross_branch_access && branchFilter !== 'all')
    ? visibleByArchive.filter(c => c.branch === branchFilter)
    : visibleByArchive;

  const copy = async (val, label) => {
    if (!val) return;
    try {
      await navigator.clipboard.writeText(val);
      // toast simple
      const t = document.createElement('div');
      t.className = 'fixed bottom-6 right-6 bg-[#008A8D] text-white px-4 py-2 rounded-lg shadow-lg z-50 text-sm';
      t.textContent = `${label || 'Copie'} : ${val}`;
      document.body.appendChild(t);
      setTimeout(() => t.remove(), 1500);
    } catch {}
  };

  return (
    <div className="p-4 lg:p-8" data-testid="clients-directory-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <BranchPageHeader user={user} baseTitle="Annuaire Clients" />
          <h1 className="text-3xl font-bold text-gray-900">Annuaire Clients</h1>
          <p className="text-gray-600 mt-1">Recherche rapide : nom, email, telephone, adresse...</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {user?.cross_branch_access && (
            <BranchFilter value={branchFilter} onChange={setBranchFilter} testidPrefix="annuaire" />
          )}
          <div className="text-sm text-gray-500">
            <span className="font-bold text-[#008A8D]">{visible.length}</span> contacts
            {count !== visible.length && <span className="ml-1 text-gray-400">/ {count} total</span>}
          </div>
        </div>
      </div>

      <div className="realease-card p-4 mb-4 flex flex-wrap gap-3 items-center sticky top-0 z-10 bg-white">
        <div className="relative flex-1 min-w-[280px]">
          <svg className="absolute left-3 top-3 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                 placeholder="Tapez un nom, email, telephone, ville, code postal..."
                 className="realease-input w-full pl-10"
                 autoFocus
                 data-testid="directory-search" />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input type="checkbox" checked={filterArchived}
                 onChange={e => setFilterArchived(e.target.checked)}
                 className="w-4 h-4 accent-[#008A8D]" />
          Inclure les archives
        </label>
      </div>

      {loading && <p className="text-xs text-gray-400 italic mb-2">Recherche...</p>}

      {visible.length === 0 ? (
        <div className="realease-card p-12 text-center text-gray-400 italic">
          {search ? `Aucun resultat pour "${search}"` : 'Aucun contact dans l\'annuaire pour le moment.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {visible.map(c => {
            const stat = STATUS_LABELS[c.status] || { label: c.status || '-', color: 'bg-gray-100 text-gray-700' };
            return (
              <div key={c.lead_id}
                   className={`realease-card p-4 hover:shadow-lg transition-shadow ${c.archived ? 'opacity-60' : ''}`}
                   data-testid={`directory-card-${c.lead_id}`}>
                <div className="flex items-start justify-between mb-2 gap-2">
                  <Link to={`/qualification/${c.lead_id}`} className="font-semibold text-gray-900 hover:text-[#008A8D] truncate">
                    {c.full_name || c.email || '(sans nom)'}
                  </Link>
                  <div className="flex items-center gap-1.5 flex-wrap justify-end">
                    {user?.cross_branch_access && c.branch && <BranchBadge branch={c.branch} size="xs" />}
                    <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${stat.color}`}>
                      {stat.label}
                    </span>
                  </div>
                </div>

                <div className="space-y-1 text-sm">
                  {c.email && (
                    <div className="flex items-center gap-2 group">
                      <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <a href={`mailto:${c.email}`} className="text-[#008A8D] hover:underline truncate font-mono text-xs flex-1">{c.email}</a>
                      <button onClick={() => copy(c.email, 'Email')} className="opacity-0 group-hover:opacity-100 text-xs text-gray-400 hover:text-[#008A8D]" title="Copier">⧉</button>
                    </div>
                  )}
                  {c.phone && (
                    <div className="flex items-center gap-2 group">
                      <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.95.68l1.49 4.47a1 1 0 01-.5 1.21l-2.26 1.13a11 11 0 005.52 5.52l1.13-2.26a1 1 0 011.2-.5l4.48 1.49a1 1 0 01.68.95V19a2 2 0 01-2 2h-1C9.72 21 3 14.28 3 6V5z" />
                      </svg>
                      <a href={`tel:${c.phone}`} className="text-gray-700 hover:text-[#008A8D] font-mono text-xs flex-1">{c.phone}</a>
                      <button onClick={() => copy(c.phone, 'Tel')} className="opacity-0 group-hover:opacity-100 text-xs text-gray-400 hover:text-[#008A8D]" title="Copier">⧉</button>
                    </div>
                  )}
                  {(c.city || c.postal_code) && (
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-gray-600 text-xs truncate">{[c.address, c.postal_code, c.city].filter(Boolean).join(', ')}</span>
                    </div>
                  )}
                </div>

                {c.projects_count > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-[11px] uppercase font-semibold text-gray-500 mb-1">
                      {c.projects_count} projet{c.projects_count > 1 ? 's' : ''}
                    </p>
                    <div className="space-y-1">
                      {c.projects.slice(0, 3).map(p => (
                        <Link key={p.project_id} to={`/projects/${p.project_id}`}
                              className="block text-xs text-[#008A8D] hover:underline truncate">
                          → {p.name} <span className="text-gray-400">({(p.status || '').replace('_', ' ')})</span>
                        </Link>
                      ))}
                      {c.projects_count > 3 && <p className="text-xs text-gray-400">+ {c.projects_count - 3} autres</p>}
                    </div>
                  </div>
                )}

                <div className="mt-3 pt-2 border-t border-gray-100 flex justify-between items-center">
                  <Link to={`/qualification/${c.lead_id}`}
                        className="text-xs font-semibold text-gray-600 hover:text-[#008A8D]">
                    Fiche complete →
                  </Link>
                  {c.has_client_account && (
                    <span className="text-[10px] px-2 py-0.5 bg-[#A57F4F]/10 text-[#A57F4F] rounded-full font-semibold">
                      Acces client actif
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClientsDirectoryPage;
